import { z } from 'zod';
import { Filter } from '@models/feature-descriptor';
import { TableColumns, Column } from '@models/column';
import { KeysOfUnion } from '@utils/types';

import {
  ColumnDraft,
  NumberDraft,
  OnlyColumnFields,
} from './common-field-draft';

type FilterColumnFields = OnlyColumnFields<Filter>;

export type FilterDraft = Partial<Record<KeysOfUnion<Filter>, any>>;

const AboveFilterDraft = z.object({
  type: z.literal(Filter.Type.Above),
  column: ColumnDraft,
  min_inclusive: NumberDraft,
});
const BelowFilterDraft = z.object({
  type: z.literal(Filter.Type.Below),
  column: ColumnDraft,
  max_exclusive: NumberDraft,
});
const BetweenFilterDraft = z.object({
  type: z.literal(Filter.Type.Between),
  column: ColumnDraft,
  min_inclusive: NumberDraft,
  max_exclusive: NumberDraft,
});
const FullPassFilterDraft = z.object({
  type: z.literal(Filter.Type.FullPass),
});
const HourOfDayFilterDraft = z.object({
  type: z.literal(Filter.Type.HourOfDay),
  column: ColumnDraft,
  min_hour_inclusive: NumberDraft,
  max_hour_exclusive: NumberDraft,
});
const NgramFilterDraft = z.object({
  type: z.literal(Filter.Type.NgramFilter),
  column: ColumnDraft,
  ngram: z.array(z.string()),
});
const ValueFilterDraft = z.object({
  type: z.literal(Filter.Type.Value),
  column: ColumnDraft,
  value: z.union([z.string(), NumberDraft, z.boolean()]),
});
const WeekdayFilterDraft = z.object({
  type: z.literal(Filter.Type.Weekday),
  column: ColumnDraft,
});
const WeekendFilterDraft = z.object({
  type: z.literal(Filter.Type.Weekend),
  column: ColumnDraft,
});

const FilterDraftUnion = z.union([
  AboveFilterDraft,
  BelowFilterDraft,
  BetweenFilterDraft,
  FullPassFilterDraft,
  HourOfDayFilterDraft,
  NgramFilterDraft,
  ValueFilterDraft,
  WeekdayFilterDraft,
  WeekendFilterDraft,
  z.lazy(() => AndFilterDraft),
]);

const AndFilterDraft = z.object({
  type: z.literal(Filter.Type.AndFilter),
  filter1: FilterDraftUnion,
  filter2: FilterDraftUnion,
  // Casting is required because of ColumnDraft type. It uses its transformed type instead of the original one
}) as unknown as z.ZodObject<any>;

const FilterDrafts = {
  [Filter.Type.AndFilter]: AndFilterDraft,
  [Filter.Type.Above]: AboveFilterDraft,
  [Filter.Type.Below]: BelowFilterDraft,
  [Filter.Type.Between]: BetweenFilterDraft,
  [Filter.Type.FullPass]: FullPassFilterDraft,
  [Filter.Type.HourOfDay]: HourOfDayFilterDraft,
  [Filter.Type.NgramFilter]: NgramFilterDraft,
  [Filter.Type.Value]: ValueFilterDraft,
  [Filter.Type.Weekday]: WeekdayFilterDraft,
  [Filter.Type.Weekend]: WeekendFilterDraft,
};

export namespace FilterDraft {
  export function fromFilter(
    filter: Filter,
    tableColumns: TableColumns
  ): FilterDraft {
    const schema = FilterDrafts[filter.type];

    const columnFields = Object.entries(schema.shape)
      .filter(([_, field]: [any, any]) => field.description === 'Column')
      .map(([colName]) => colName) as Array<keyof FilterColumnFields>;

    return filter.type === Filter.Type.AndFilter
      ? {
          type: Filter.Type.AndFilter,
          filter1: fromFilter(filter.filter1, tableColumns),
          filter2: fromFilter(filter.filter2, tableColumns),
        }
      : {
          ...filter,
          ...Object.fromEntries(
            columnFields.map(field => [
              field,
              Column.fromFullName(filter[field], tableColumns) ?? {},
            ])
          ),
        };
  }

  export function validate(filterDraft: FilterDraft) {
    if (!filterDraft.type) {
      return null;
    }
    const schema = FilterDrafts[filterDraft.type as Filter.Type];
    return schema?.safeParse(filterDraft);
  }
}
